import React, { useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { fetchData } from "../../api/client";

function confidenceLabel(confidence) {
  if (!confidence || typeof confidence !== "object") return null;
  const vals = Object.values(confidence)
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
  if (!vals.length) return null;
  const max = Math.max(...vals);
  if (max >= 0.9) return { key: "high", label: "Alta" };
  if (max >= 0.78) return { key: "medium", label: "Média" };
  return { key: "low", label: "Baixa" };
}

const CONFIDENCE_COLOR = { high: "success", medium: "info", low: "default" };

/**
 * Barra sutil de sugestão de tipo/subtipo (não bloqueia a operação).
 */
export default function TypeClassificationSuggestionBar({
  conversationId,
  suggestion,
  onApplied,
  onDismissed,
}) {
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setHidden(false);
  }, [conversationId, suggestion]);

  if (hidden || !suggestion?.chain || !conversationId) return null;

  const label =
    suggestion.label ||
    [
      suggestion.chain.id_tipo,
      suggestion.chain.id_subtipo1,
      suggestion.chain.id_subtipo2,
      suggestion.chain.id_subtipo3,
    ]
      .filter((x) => x != null && Number(x) > 0)
      .join(" › ");

  const conf = confidenceLabel(suggestion.confidence);

  const handleAccept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await fetchData(
        `/requests/${conversationId}/type-suggestion/accept`,
        "POST",
        {}
      );
      setHidden(true);
      onApplied?.(suggestion);
    } catch (e) {
      console.warn("type suggestion accept:", e?.message || e);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await fetchData(
        `/requests/${conversationId}/type-suggestion/dismiss`,
        "POST",
        {}
      );
      setHidden(true);
      onDismissed?.(suggestion);
    } catch (e) {
      console.warn("type suggestion dismiss:", e?.message || e);
      setHidden(true);
      onDismissed?.(suggestion);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        mb: 1,
        px: 1,
        py: 0.75,
        borderRadius: 1.5,
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
        <AutoAwesomeOutlinedIcon color="primary" fontSize="small" />
        <Typography variant="caption" fontWeight={700} sx={{ mr: 0.5 }}>
          Sugestão de classificação
        </Typography>
        {conf && (
          <Chip
            size="small"
            color={CONFIDENCE_COLOR[conf.key] || "default"}
            variant={conf.key === "low" ? "outlined" : "filled"}
            label={conf.label}
            sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
          />
        )}
        <Typography variant="body2" sx={{ flex: "1 1 140px", minWidth: 0 }} noWrap title={label}>
          {label}
        </Typography>
        <Button
          size="small"
          variant="contained"
          disabled={busy}
          onClick={handleAccept}
          sx={{ textTransform: "none", fontWeight: 700, py: 0.15 }}
        >
          Aplicar
        </Button>
        <Button
          size="small"
          variant="text"
          disabled={busy}
          onClick={handleDismiss}
          sx={{ textTransform: "none", py: 0.15 }}
        >
          Dispensar
        </Button>
      </Stack>
      {suggestion.reasoning ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
          {suggestion.reasoning}
        </Typography>
      ) : null}
    </Box>
  );
}
